import { Drawer as DrawerPrimitive } from "@base-ui/react/drawer";
import * as React from "react";

import { cn } from "../../utils/cn";

import { textVariants } from "./text";

/**
 * Solomon Drawer — uses Base UI's native Drawer primitive (not vaul like
 * shadcn's canonical recipe). Base UI has a Drawer primitive with built-in
 * swipe-to-dismiss (via Emil Kowalski collab). Project-level deviation
 * from shadcn base-nova.
 *
 * Base UI requires `swipeDirection` on Root for gesture wiring and Popup
 * wrapped in Viewport for touch locking — without these, drag-to-close is
 * inert. Consumers can override `swipeDirection` for left/right drawers.
 */

function Drawer({
    swipeDirection = "down",
    ...props
}: DrawerPrimitive.Root.Props): React.ReactElement {
    return (
        <DrawerPrimitive.Root
            data-slot="drawer"
            swipeDirection={swipeDirection}
            {...props}
        />
    );
}

function DrawerTrigger({
    ...props
}: DrawerPrimitive.Trigger.Props): React.ReactElement {
    return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />;
}

function DrawerPortal({
    ...props
}: DrawerPrimitive.Portal.Props): React.ReactElement {
    return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />;
}

function DrawerClose({
    ...props
}: DrawerPrimitive.Close.Props): React.ReactElement {
    return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />;
}

function DrawerOverlay({
    className,
    ...props
}: DrawerPrimitive.Backdrop.Props): React.ReactElement {
    return (
        <DrawerPrimitive.Backdrop
            data-slot="drawer-overlay"
            className={cn(
                "fixed inset-0 z-40 bg-surface-dialog-base supports-backdrop-filter:backdrop-blur-sm",
                // Use CSS transitions (not tailwindcss-animate keyframes) so the
                // backdrop fade is on the same 300ms timeline as the popup
                // transform — otherwise the 150ms keyframe ends, opacity snaps
                // back, and the blur flashes during the remaining popup close.
                "transition-opacity duration-300 ease-out",
                "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0",
                className
            )}
            {...props}
        />
    );
}

function DrawerContent({
    className,
    children,
    ...props
}: DrawerPrimitive.Popup.Props): React.ReactElement {
    return (
        <DrawerPortal>
            <DrawerOverlay />
            <DrawerPrimitive.Viewport className="fixed inset-0 z-50 flex flex-col justify-end pointer-events-none">
                <DrawerPrimitive.Popup
                    data-slot="drawer-content"
                    className={cn(
                        "group/drawer-content pointer-events-auto flex max-h-[90vh] flex-col bg-surface-overlay border-t-2 border-accent font-sans text-foreground-base pb-[env(safe-area-inset-bottom)] outline-none",
                        "transition-transform duration-300 ease-out",
                        "data-[starting-style]:translate-y-full data-[ending-style]:translate-y-full",
                        className
                    )}
                    {...props}
                >
                    {/* Drag handle — swipe-to-dismiss affordance.
                        Solomon style: sharp corners, line-base tone,
                        tight gap to the title beneath it. */}
                    <div className="mx-auto mt-3 mb-1 h-1 w-12 shrink-0 bg-line-base" />
                    {children}
                </DrawerPrimitive.Popup>
            </DrawerPrimitive.Viewport>
        </DrawerPortal>
    );
}

function DrawerHeader({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
    return (
        <div
            data-slot="drawer-header"
            className={cn(
                "flex flex-col gap-1.5 px-6 pt-0 pb-4 text-left",
                className
            )}
            {...props}
        />
    );
}

function DrawerFooter({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
    return (
        <div
            data-slot="drawer-footer"
            className={cn("mt-auto flex flex-col gap-2 px-6 py-4", className)}
            {...props}
        />
    );
}

function DrawerTitle({
    className,
    ...props
}: DrawerPrimitive.Title.Props): React.ReactElement {
    return (
        <DrawerPrimitive.Title
            data-slot="drawer-title"
            className={cn(
                textVariants({ variant: "h2" }),
                "text-foreground-base",
                className
            )}
            {...props}
        />
    );
}

function DrawerDescription({
    className,
    ...props
}: DrawerPrimitive.Description.Props): React.ReactElement {
    return (
        <DrawerPrimitive.Description
            data-slot="drawer-description"
            className={cn(
                textVariants({ variant: "body" }),
                "text-foreground-neutral",
                className
            )}
            {...props}
        />
    );
}

export {
    Drawer,
    DrawerTrigger,
    DrawerPortal,
    DrawerClose,
    DrawerOverlay,
    DrawerContent,
    DrawerHeader,
    DrawerFooter,
    DrawerTitle,
    DrawerDescription
};
